import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isPast, toDateObject} from "../../../system/utils";
import Icon from "../../../system/components/icon";
import {OverlayPanel} from "primereact/overlaypanel";
import ZofinoDatepicker from "../../../system/components/zofino-datepicker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import Span from "../../../system/components/span";
import {useTranslate} from "../../../system/useTranslate";
import TaskRecurrenceControl, {createDefaultRecurrence} from "./task-recurrence-control";
import ScheduleAssistant from "./schedule-assistant/schedule-assistant";

// `assistant` = {assignee, excludeTaskKey}: the task sidebar opens «دستیار برنامه‌ریزی کار» together with the picker; a
// chosen slot fills the picker's draft and is saved only with «اعمال» (boss's task «زمانبندی کار تغییر کنه»)
function SelectZofinoDateTime({selectedDateTime, onChange, range=false, hideLabels, enableRecurrence=false, fullWidth=false, recurrence, onRecurrenceChange, recurrenceDisabled=false, onPanelHide, assistant}) {
    const ref = useRef();
    const triggerRef = useRef();
    const [panelOpen, setPanelOpen] = useState(false);
    const translate = useTranslate();
    const [localRecurrence, setLocalRecurrence] = useState(createDefaultRecurrence);
    // Controlled when the parent passes `recurrence`, otherwise local (picker-only use, e.g. the delay modal).
    const controlled = recurrence !== undefined && recurrence !== null;
    const recurrenceValue = controlled ? recurrence : localRecurrence;
    const showRecurrence = enableRecurrence && !recurrenceDisabled;
    const [pickerSession, setPickerSession] = useState(0);
    const [draft, setDraft] = useState({start: null, end: null});
    const [externalDraft, setExternalDraft] = useState(null);
    const requestedActiveFieldRef = useRef(null);
    const startDate = selectedDateTime?.start;
    const endDate = selectedDateTime?.end;
    const datePickerValue = useMemo(() => ({
        start: startDate ? toDateObject(startDate * 1000) : null,
        end: endDate ? toDateObject(endDate * 1000) : null,
    }), [startDate, endDate]);
    const datePickerConfig = useMemo(
        () => ({calendar: persian, locale: persian_fa, range}),
        [range]
    );
    // The panel stays open for every action inside it (calendar cells re-render and detach their DOM node, and nested
    // dropdown panels live in a portal), so PrimeReact's own outside-click check is replaced by this one.
    useEffect(() => {
        if (!panelOpen) return undefined;
        const onMouseDown = (event) => {
            const target = event.target;
            const panel = ref.current?.getElement?.();
            if (panel?.contains(target) || triggerRef.current?.contains(target)) return;
            // the hour list («افزودن ساعت») is a floating layer of its own: picking an hour closed the whole picker and the
            // time was never applied (boss's bug report 2026-10-28)
            if (target?.closest?.(".p-dropdown-panel, .p-multiselect-panel, .p-overlay, .rmdp-wrapper, .rmdp-container, [data-zofino-floating-layer]")) return;
            ref.current?.hide();
        };
        document.addEventListener("mousedown", onMouseDown, true);
        return () => document.removeEventListener("mousedown", onMouseDown, true);
    }, [panelOpen]);

    // Recurrence is saved on the task (task series); the picker applies it together with the date.
    const handleRecurrenceChange = useCallback((nextRecurrence) => {
        if (!controlled) setLocalRecurrence(nextRecurrence);
        onRecurrenceChange?.(nextRecurrence);
    }, [controlled, onRecurrenceChange]);
    const renderRecurrence = useCallback(({value, onChange: onDraftChange, startWeekDay, startDate: draftStartDate}) => (
        <TaskRecurrenceControl
            value={value}
            onChange={onDraftChange}
            startWeekDay={startWeekDay}
            startDate={draftStartDate}
        />
    ), []);
    const openPicker = useCallback((event, field) => {
        event?.stopPropagation?.();
        const nextField = field || (range && !startDate ? "start" : "end");
        requestedActiveFieldRef.current = nextField;
        ref.current?.toggle(event);
    }, [range, startDate]);
    const handleFieldKeyDown = useCallback((event, field) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPicker(event, field);
        }
    }, [openPicker]);

    return(
        <div className={`flex align-items-center gap-1 cursor-pointer border-round p-2 px-1 ${fullWidth ? "w-full" : ""} ${endDate && isPast(new Date(endDate * 1000).getTime() / 1000, 1, "days") ? "text-red-500" : ""}`} ref={triggerRef} onClick={(event) => openPicker(event)}>
            <Icon icon="mi mi:event" className="text-lg"/>
            {enableRecurrence && recurrenceValue?.frequency && recurrenceValue.frequency !== "none" && (
                <span className="flex align-items-center" title={translate(`date-picker.recurrence.${recurrenceValue.frequency}`)}><Icon icon="mi mi:repeat" className="text-sm text-primary"/></span>
            )}
            {!hideLabels&&(
                <div className={`flex gap-1 align-items-center text-xs ${fullWidth ? "flex-1" : ""}`}>

                    {selectedDateTime?(
                        <>
                            {range&&(
                                <>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={translate("date-picker.start-label")}
                                        className="border-round-sm"
                                        onClick={(event) => openPicker(event, "start")}
                                        onKeyDown={(event) => handleFieldKeyDown(event, "start")}
                                    >
                                        {startDate
                                            ? <Span spanClassName="text-3xs p-1" format="dddd، DD MMMM YYYY" type="PERSIAN_DATE">{startDate * 1000}</Span>
                                            : translate("from-date")}
                                    </span>
                                    <Icon icon="mi mi:arrow_left_alt" className="text-xs"/>
                                </>
                            )}
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label={translate("date-picker.end-label")}
                                className="border-round-sm"
                                onClick={(event) => openPicker(event, "end")}
                                onKeyDown={(event) => handleFieldKeyDown(event, "end")}
                            >
                                {endDate
                                    ? <Span spanClassName="text-3xs p-1" format="dddd، DD MMMM YYYY" type="PERSIAN_DATE">{endDate * 1000}</Span>
                                    : translate("to-date")}
                            </span>
                        </>
                    ):(<span>{translate("dueDate")}</span>)}



                    {/*{*/}
                    {/*    selectedDateTime?.[0]&&(*/}
                    {/*        <>*/}
                    {/*            <Span spanClassName="text-xs p-1" format="dddd، DD MMMM YYYY" type="PERSIAN_DATE">{selectedDateTime?.[0]}</Span>*/}
                    {/*            <span className="text-xs"> تا </span>*/}
                    {/*        </>*/}
                    {/*    )*/}
                    {/*}*/}

                    {/*<Span spanClassName="text-xs p-1" format="dddd، DD MMMM YYYY" type="PERSIAN_DATE">{selectedDateTime?.[1]}</Span>*/}
                </div>
            )}
            <OverlayPanel
                pt={{root:()=>({className:"p-0 max-w-full rtl"}),content:()=>({className:"p-0 max-w-full"})}}
                ref={ref}
                dismissable={false}
                onShow={() => { setPanelOpen(true); setPickerSession((session) => session + 1); }}
                onHide={() => { setPanelOpen(false); onPanelHide?.(); }}
            >
                <div
                    className="flex flex-column max-w-full"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="flex align-items-stretch max-w-full">
                    <ZofinoDatepicker
                        value={datePickerValue}
                        config={datePickerConfig}
                        onChange={onChange}
                        recurrence={showRecurrence ? recurrenceValue : null}
                        onRecurrenceChange={showRecurrence ? handleRecurrenceChange : undefined}
                        renderRecurrence={showRecurrence ? renderRecurrence : undefined}
                        sessionKey={pickerSession}
                        initialActiveField={requestedActiveFieldRef.current}
                        onApply={() => ref.current?.hide()}
                        onCancel={() => ref.current?.hide()}
                        onDraftChange={assistant ? setDraft : undefined}
                        externalDraft={externalDraft}
                    />
                    {assistant && panelOpen && <ScheduleAssistant
                        compact
                        saveLabel="اعمال"
                        assignee={assistant.assignee}
                        excludeTaskKey={assistant.excludeTaskKey}
                        start={draft.start}
                        end={draft.end}
                        onPick={({start, end}) => setExternalDraft({start: toDateObject(start * 1000), end: toDateObject(end * 1000), nonce: Date.now()})}
                    />}
                    </div>
                </div>
            </OverlayPanel>


            {/*<Span focused={focused} setFocused={setFocused} inputValue={new Date(selectedDateTime).getTime()/1000} inputClassName="text-xs bg-transparent" spanClassName="text-xs p-1" format="dddd، DD MMMM YYYY" type="PERSIAN_DATE" onChange={(e)=>{onChange({value:new Date(e.value*1000)})}}>{selectedDateTime}</Span>*/}
        </div>
    )
}
export default SelectZofinoDateTime;
